import { empty, linkBtn } from './components';
import { h } from './dom';

/** Hash router: `#/leads/:id?x=1`. Views render into a container and may return a cleanup fn. */
export interface RouteContext {
  params: Record<string, string>;
  query: URLSearchParams;
  path: string;
  root: HTMLElement;
}

export type ViewCleanup = () => void;
export type View = (ctx: RouteContext) => Promise<ViewCleanup | void> | ViewCleanup | void;

export interface RouteDef {
  pattern: string;
  view: View;
}

export const parseHash = (hash: string): { path: string; query: URLSearchParams } => {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const [pathPart = '', queryPart = ''] = raw.split('?');
  const path = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
  return {
    path: path === '/' ? '/home' : path.replace(/\/+$/, '') || '/home',
    query: new URLSearchParams(queryPart),
  };
};

/** A malformed %-escape (hand-edited URL) stays raw; decodeURIComponent would throw and blank the app. */
const decodeParam = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

export const matchRoute = (pattern: string, path: string): Record<string, string> | null => {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const pp = p[i] as string;
    const sp = s[i] as string;
    if (pp.startsWith(':')) params[pp.slice(1)] = decodeParam(sp);
    else if (pp !== sp) return null;
  }
  return params;
};

export const navigate = (to: string, opts: { replace?: boolean } = {}): void => {
  const target = to.startsWith('#') ? to : `#${to}`;
  if (opts.replace) history.replaceState(null, '', target);
  else location.hash = target;
  if (opts.replace) window.dispatchEvent(new HashChangeEvent('hashchange'));
};

export const currentPath = (): string => parseHash(location.hash).path;

const NEXT_KEY = 'sb.next';

/** The auth gate bounced this hash to the login screen: keep it for the sign-in to return to, so the
 *  Send to CRM landing route (D62) survives the detour. Home and the login screen itself are not kept. */
export const rememberNext = (hash: string): void => {
  const { path } = parseHash(hash);
  if (path === '/login' || path === '/home') return;
  try {
    sessionStorage.setItem(NEXT_KEY, hash);
  } catch {
    // storage unavailable: sign-in falls back to Home
  }
};

/** The hash to open after sign-in, consumed once. */
export const takeNext = (): string | null => {
  try {
    const next = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    return next;
  } catch {
    return null;
  }
};

/** A view that threw (a local database another tab closed, a storage read the browser blocked) gets a
 *  message and a way back instead of a blank screen under the last one's title. */
const failedView = (err: unknown): HTMLElement =>
  h(
    'div',
    { class: 'page page--error' },
    empty('This screen could not open', err instanceof Error ? err.message : String(err)),
    linkBtn('Home', '#/home', { variant: 'primary' }),
  );

export interface Router {
  start: () => void;
  stop: () => void;
  /** Re-render the current route (e.g. after auth state change). */
  refresh: () => void;
}

export const createRouter = (
  root: HTMLElement,
  routes: RouteDef[],
  opts: {
    notFound: View;
    beforeEach?: (path: string) => string | void;
    afterRender?: (path: string, query: URLSearchParams) => void;
    /** A view threw: the shell's title and chrome are the previous screen's until this resets them. */
    onError?: (path: string, err: unknown) => void;
  },
): Router => {
  let cleanup: ViewCleanup | void;
  let token = 0;

  const render = async () => {
    const { path: rawPath, query } = parseHash(location.hash);
    const redirect = opts.beforeEach?.(rawPath);
    if (redirect && redirect !== rawPath) {
      navigate(redirect, { replace: true });
      return;
    }
    const path = rawPath;
    const mine = ++token;
    if (cleanup) cleanup();
    cleanup = undefined;
    root.replaceChildren();
    root.scrollTop = 0;
    window.scrollTo(0, 0);
    let view: View = opts.notFound;
    let params: Record<string, string> = {};
    for (const r of routes) {
      const m = matchRoute(r.pattern, path);
      if (m) {
        view = r.view;
        params = m;
        break;
      }
    }
    let result: ViewCleanup | void;
    try {
      result = await view({ params, query, path, root });
    } catch (err) {
      if (mine !== token) return;
      console.error(`${path} could not open`, err);
      opts.onError?.(path, err);
      root.replaceChildren(failedView(err));
      opts.afterRender?.(path, query);
      return;
    }
    if (mine !== token) {
      if (typeof result === 'function') result();
      return;
    }
    cleanup = result;
    opts.afterRender?.(path, query);
  };

  const onHash = () => void render();
  return {
    start: () => {
      window.addEventListener('hashchange', onHash);
      void render();
    },
    stop: () => window.removeEventListener('hashchange', onHash),
    refresh: () => void render(),
  };
};
